import {
  Alert,
  Backdrop,
  CircularProgress,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material'
import { SyntheticEvent, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { dismissMicroLearning } from '../../api/api'
import { DismissReasonId, ExperimentDisposition } from '../../api/models'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'
import styles from './MicroLearningDetail.module.css'
import ApproveModal from './components/ApproveModal'
import DetailHeader from './components/DetailHeader'
import DetailTabsContent, { DETAIL_TAB_CONFIG, DetailTabValue } from './components/DetailTabsContent'
import DismissModal from './components/DismissModal'
import ExperimentDispositionModal from './components/ExperimentDispositionModal'
import { useExport } from './hooks/useExport'
import { useMicroLearningDetail } from './hooks/useMicroLearningDetail'

const MicroLearningDetail = () => {
  const navigate = useNavigate()
  const { mlId = '' } = useParams()
  const appStateContext = useContext(AppStateContext)
  const [activeTab, setActiveTab] = useState<DetailTabValue>('summary')
  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [dismissStep, setDismissStep] = useState<'closed' | 'warning' | 'confirm' | 'disposition'>('closed')
  const [dismissReason, setDismissReason] = useState<DismissReasonId | ''>('')
  const [dismissContext, setDismissContext] = useState('')
  const [isDismissing, setIsDismissing] = useState(false)
  const [experimentDispositions, setExperimentDispositions] = useState<
    Record<string, ExperimentDisposition | undefined>
  >({})
  const [historyRefreshSignal, setHistoryRefreshSignal] = useState(0)
  const {
    isLoading,
    hasError,
    isNotFound,
    data,
    showScalePotential,
    notProcessed,
    reviewDecision,
    refetch,
    patchData,
  } = useMicroLearningDetail(mlId)
  const { isExporting, triggerExport } = useExport(mlId)

  useEffect(() => {
    if (!showScalePotential && activeTab === 'scale-potential') {
      setActiveTab('summary')
    }
  }, [activeTab, showScalePotential])

  const visibleTabs = useMemo(
    () => DETAIL_TAB_CONFIG.filter(tab => showScalePotential || tab.value !== 'scale-potential'),
    [showScalePotential],
  )

  const refreshHistory = useCallback(() => {
    setHistoryRefreshSignal(s => s + 1)
  }, [])

  const handleEditSuccess = useCallback(
    (partial: Partial<typeof data>) => {
      patchData(partial as any)
      refreshHistory()
    },
    [patchData, refreshHistory],
  )

  const handleApproveSuccess = useCallback(() => {
    refetch()
    refreshHistory()
  }, [refetch, refreshHistory])

  const handleRegenerateSuccess = useCallback(() => {
    refetch()
    refreshHistory()
  }, [refetch, refreshHistory])

  const handleTabChange = (_: SyntheticEvent, newValue: DetailTabValue) => {
    setActiveTab(newValue)
  }

  const tabLabel = (tabValue: DetailTabValue, label: string) => {
    if (tabValue === 'experiments') {
      return (
        <Tooltip title='Experiments used to generate this micro-learning'>
          <span>{label}</span>
        </Tooltip>
      )
    }

    if (tabValue === 'scale-potential') {
      return (
        <Tooltip title='Additional experiments that could strengthen this learning or help it progress toward a Best Practice Decoded'>
          <span>{label}</span>
        </Tooltip>
      )
    }

    return label
  }

  const resetDismissFlow = () => {
    setDismissStep('closed')
    setDismissReason('')
    setDismissContext('')
    setIsDismissing(false)
    setExperimentDispositions({})
  }

  const openDismissFlow = () => {
    const emptyDispositions = Object.fromEntries((data?.experiments ?? []).map(experiment => [experiment.code, undefined]))
    setExperimentDispositions(emptyDispositions)
    setDismissStep(data?.status === 'approved' ? 'warning' : 'confirm')
  }

  const handleDismissSave = async () => {
    if (!dismissReason || !data || !mlId) return
    const payloadDispositions = data.experiments
      .map(experiment => {
        const disposition = experimentDispositions[experiment.code]
        if (!disposition) return null
        return { code: experiment.code, disposition }
      })
      .filter((item): item is { code: string; disposition: ExperimentDisposition } => item !== null)

    if (payloadDispositions.length !== data.experiments.length) {
      return
    }

    setIsDismissing(true)
    const result = await dismissMicroLearning(mlId, {
      selected_reason: dismissReason,
      reason: dismissContext.trim() || undefined,
      experiment_dispositions: payloadDispositions,
    })
    setIsDismissing(false)

    if (result.success) {
      resetDismissFlow()
      refetch()
      refreshHistory()
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Micro-learning dismissed successfully', severity: 'success' },
      })
      return
    }

    appStateContext?.dispatch({
      type: 'SET_SNACKBAR_STATE',
      payload: {
        message: 'Failed saving Micro-learning changes. Try again later',
        severity: 'warning',
      },
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Micro Learnings</h1>
        <p className={styles.headerDescription}>
          Review the AI-generated micro-learning and decide how to proceed.
        </p>
      </div>

      <div className={styles.breadcrumb}>
        <button
          type='button'
          className={styles.breadcrumbLink}
          onClick={() => navigate('/app/micro-learnings')}>
          Micro Learnings
        </button>
        <span className={styles.breadcrumbSeparator} aria-hidden='true'>
          {'>'}
        </span>
        <span className={styles.breadcrumbCurrent}>Micro-Learning Detail</span>
      </div>
      {hasError ? (
        <Alert severity='error'>
          {isNotFound
            ? 'This micro-learning could not be found.'
            : 'Unable to load micro-learning detail. Please try again.'}
        </Alert>
      ) : (
        <>
          <DetailHeader
            data={data}
            reviewDecision={reviewDecision}
            onApproveClick={() => setApproveModalOpen(true)}
            onEditSuccess={handleEditSuccess}
            onRegenerateSuccess={handleRegenerateSuccess}
            isExporting={isExporting}
            triggerExport={triggerExport}
            onDismissClick={openDismissFlow}
          />
          <div className={styles.tabsShell}>
            <Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
              {visibleTabs.map(tab => (
                <Tab key={tab.value} value={tab.value} label={tabLabel(tab.value, tab.label)} />
              ))}
            </Tabs>

            <DetailTabsContent activeTab={activeTab} data={data} notProcessed={notProcessed} historyRefreshSignal={historyRefreshSignal} />
          </div>
        </>
      )}

      <ApproveModal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        mlId={mlId}
        data={data}
        onSuccess={handleApproveSuccess}
      />
      <CustomDialog
        title='Attention'
        open={dismissStep === 'warning'}
        setClose={resetDismissFlow}
        primaryAction={() => setDismissStep('confirm')}
        secondaryAction={resetDismissFlow}
        primaryActionButtonText='Continue'
        secondaryActionButtonText='Cancel'>
        <p className={styles.attentionDialogBody}>
          This micro-learning is currently <strong>approved</strong>. Dismiss will revert it.
        </p>
      </CustomDialog>
      <DismissModal
        open={dismissStep === 'confirm'}
        data={data}
        selectedReason={dismissReason}
        additionalContext={dismissContext}
        onSelectedReasonChange={setDismissReason}
        onAdditionalContextChange={setDismissContext}
        onClose={resetDismissFlow}
        onContinue={() => setDismissStep('disposition')}
      />
      <ExperimentDispositionModal
        open={dismissStep === 'disposition'}
        data={data}
        dispositions={experimentDispositions}
        isSaving={isDismissing}
        onSetDisposition={(code, disposition) => {
          setExperimentDispositions(prev => ({ ...prev, [code]: disposition }))
        }}
        onSetAllDisposition={disposition => {
          setExperimentDispositions(Object.fromEntries((data?.experiments ?? []).map(experiment => [experiment.code, disposition])))
        }}
        onClose={resetDismissFlow}
        onSave={handleDismissSave}
      />
      <CustomDialog
        title='Attention'
        open={dismissStep === 'warning'}
        setClose={resetDismissFlow}
        primaryAction={() => setDismissStep('confirm')}
        secondaryAction={resetDismissFlow}
        primaryActionButtonText='Continue'
        secondaryActionButtonText='Cancel'>
        <p className={styles.attentionDialogBody}>
          This micro-learning is currently <strong>approved</strong>. Dismiss will revert it.
        </p>
      </CustomDialog>
      <DismissModal
        open={dismissStep === 'confirm'}
        data={data}
        selectedReason={dismissReason}
        additionalContext={dismissContext}
        onSelectedReasonChange={setDismissReason}
        onAdditionalContextChange={setDismissContext}
        onClose={resetDismissFlow}
        onContinue={() => setDismissStep('disposition')}
      />
      <ExperimentDispositionModal
        open={dismissStep === 'disposition'}
        data={data}
        dispositions={experimentDispositions}
        isSaving={isDismissing}
        onSetDisposition={(code, disposition) => {
          setExperimentDispositions(prev => ({ ...prev, [code]: disposition }))
        }}
        onSetAllDisposition={disposition => {
          setExperimentDispositions(Object.fromEntries((data?.experiments ?? []).map(experiment => [experiment.code, disposition])))
        }}
        onClose={resetDismissFlow}
        onSave={handleDismissSave}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </div>
  )
}

export default MicroLearningDetail
